import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './participanteformpage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useParticipanteService } from '../../services/participanteServices';

function ParticipanteFormPage() {
    const { logout } = useAuth();
    const { showToast } = useToast();
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();
    const { idEmpresa, idTurma, idParticipante } = useParams();
    const { pathname } = useLocation();

    const { obterEmpresaPorId } = useEmpresaService();
    const { obterTurmaPorId } = useTurmaService();
    const { obterParticipantePorId, obterParticipantePorCelular, criarParticipante, atualizarParticipante } = useParticipanteService();

    const isEditing = !!idParticipante && pathname.includes('atualizar');

    const [empresa, setEmpresa] = useState(null);
    const [turma, setTurma] = useState(null);

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        celular: '',
        senha: ''
    });

    // 📱 Função de máscara de celular
    const formatarCelular = (valor) => {
        const numeros = valor.replace(/\D/g, '').slice(0, 11);
        const parte1 = numeros.slice(0, 2);
        const parte2 = numeros.slice(2, 7);
        const parte3 = numeros.slice(7, 11);

        if (numeros.length <= 2) return `(${parte1}`;
        if (numeros.length <= 7) return `(${parte1}) ${parte2}`;
        return `(${parte1}) ${parte2}-${parte3}`;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'celular') {
            const numeros = value.replace(/\D/g, '');
            setFormData((prev) => ({
                ...prev,
                celular: formatarCelular(numeros)
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        showLoader(isEditing ? 'Atualizando participante' : 'Criando participante');

        const celularSemMascara = formData.celular.replace(/\D/g, '');

        if (celularSemMascara.length !== 11) {
            showToast('O celular deve conter 11 dígitos!', 3000, 'error');
            hideLoader();
            return;
        }

        if (!isEditing) {
            const celularCheck = await obterParticipantePorCelular(celularSemMascara);
            if (celularCheck.success && celularCheck.data) {
                showToast('Já existe um participante com esse celular.', 3000, 'error');
                hideLoader();
                return;
            }
        }

        const participanteJson = {
            ...formData,
            celular: celularSemMascara,
            loginPor: 'celular',
            idTurma: idTurma,
        };

        if (isEditing) participanteJson.id = idParticipante;

        const response = isEditing
            ? await atualizarParticipante(participanteJson)
            : await criarParticipante(participanteJson);

        if (response.success) {
            showToast(
                isEditing ? 'Participante atualizado com sucesso!' : 'Participante criado com sucesso!',
                3000,
                'success'
            );
            navigate(`/empresas/${idEmpresa}/${idTurma}`);
        } else {
            showToast(
                isEditing ? 'Erro ao atualizar participante.' : 'Erro ao criar participante.',
                3000,
                'error'
            );
            if (response.message?.toLowerCase().includes('token')) logout();
        }

        hideLoader();
    };

    useEffect(() => {
        const carregarDados = async () => {
            showLoader('Carregando dados');

            const empresaResp = await obterEmpresaPorId(idEmpresa);
            if (empresaResp.success) {
                setEmpresa(empresaResp.data);
            } else {
                showToast('Erro ao carregar empresa', 3000, 'error');
                if (empresaResp.message?.toLowerCase().includes('token')) logout();
                hideLoader();
                return;
            }

            const turmaResp = await obterTurmaPorId(idTurma);
            if (turmaResp.success) {
                setTurma(turmaResp.data);
            } else {
                showToast('Erro ao carregar turma', 3000, 'error');
                if (turmaResp.message?.toLowerCase().includes('token')) logout();
                hideLoader();
                return;
            }

            if (isEditing) {
                const participanteResp = await obterParticipantePorId(idParticipante);
                if (participanteResp.success) {
                    const p = participanteResp.data;
                    const celularFormatado = formatarCelular(p.celular || '');

                    setFormData({
                        nome: p.nome || '',
                        email: p.email || '',
                        celular: celularFormatado,
                        senha: ''
                    });
                } else {
                    showToast('Erro ao carregar participante', 3000, 'error');
                    if (participanteResp.message?.toLowerCase().includes('token')) logout();
                }
            }

            hideLoader();
        };

        if (idEmpresa && idTurma) carregarDados();
    }, [idEmpresa, idTurma, idParticipante, isEditing]);

    const breadcrumbIds = [];
    if (empresa) breadcrumbIds.push({ id: idEmpresa, label: empresa.nome, place: 1 });
    if (turma) breadcrumbIds.push({ id: idTurma, label: turma.nome, place: 2 });
    if (isEditing && formData.nome) breadcrumbIds.push({ id: idParticipante, label: formData.nome, place: 3 });

    return (
        <div className={styles.ParticipanteFormPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbIds} />
                <h1 className={styles.title}>{isEditing ? 'Editar Participante' : 'Cadastro de Participante'}</h1>
                <FriendlyPhrase page={isEditing ? 'atualizarParticipante' : 'adicionarParticipante'} />
            </div>

            <form onSubmit={handleSubmit} className={styles.formulario}>
                <label>Nome:
                    <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>E-mail:
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>Celular:
                    <input
                        type="tel"
                        name="celular"
                        value={formData.celular}
                        onChange={handleChange}
                        required
                        placeholder="(11) 91234-5678"
                    />
                </label>

                <label>Senha:
                    <input
                        type="password"
                        name="senha"
                        value={formData.senha}
                        onChange={handleChange}
                        placeholder={isEditing ? 'A senha antiga não pode ser exibida' : ''}
                    />
                </label>

                <div className={styles.submitArea}>
                    <button type="submit" className={styles.submitButton}>
                        {isEditing ? 'Atualizar Participante' : 'Salvar Participante'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ParticipanteFormPage;